import type { RefObject } from 'react';
import type { AnyRouter } from '@tanstack/react-router';

/**
 * Per-navigation view-transition-name generator for restaurant-card ->
 * restaurant-detail shared-element transitions (D45c Phase 4). Names are
 * scoped by restaurant ID; combined with `assignViewTransitionName` only
 * ever writing to the tapped card's own DOM node right before `navigate()`,
 * this keeps names unique across concurrent or rapid-succession taps — no
 * other on-screen card instance ever carries a name at the same time.
 */
export function getRestaurantTransitionNames(restaurantId: number) {
  return {
    photo: `restaurant-photo-${restaurantId}`,
    title: `restaurant-title-${restaurantId}`,
  };
}

export function assignViewTransitionName<T extends HTMLElement>(ref: RefObject<T | null>, name: string) {
  ref.current?.style.setProperty('view-transition-name', name);
}

/*
 * ---------------------------------------------------------------------------
 * Reverse (profile -> card) shared-element transition
 * ---------------------------------------------------------------------------
 * TanStack Router (verified against @tanstack/router-core 1.139): a per-call
 * `viewTransition` option only exists on push/replace navigations. History
 * traversals (`router.history.back()`, the browser's back button / swipe)
 * are popstate-driven and consult ONLY `router.options.defaultViewTransition`
 * when the router calls `document.startViewTransition()`.
 *
 * So `startBackTransition` (hero arrow) and `installBackTransition` (browser
 * back button/swipe) arm a backward navigation away from a profile and flips `defaultViewTransition` to `true`
 * for exactly that one navigation (restored shortly after it resolves). The
 * profile hero already carries this restaurant's photo/title names, so it is
 * the "old" half of the morph; the "new" half is the card that claims the same
 * names on mount via `claimReturnTransitionNames`. If no card for that
 * restaurant mounts (filtered out, other route, still loading) nothing claims
 * the names and the browser simply plays the page-level transition.
 *
 * Why not leave `defaultViewTransition` on permanently: unspecified pushes
 * (e.g. profile -> another route) would start animating too.
 */

/** Restaurant whose card should receive the reverse-morph names; null when no back transition is in flight. */
let pendingReturnRestaurantId: number | null = null;
let disarmActive: (() => void) | null = null;
/** Card nodes that took the reverse-morph names; released on disarm so no stale names linger. */
let claimedNodes: HTMLElement[] = [];

/** Long enough for the 320ms shared-element duration plus a late React commit. */
const RETURN_CLEANUP_DELAY_MS = 800;
/** Upper bound if the back navigation never resolves (blocked / cancelled). */
const RETURN_SAFETY_TIMEOUT_MS = 3000;

function canRunViewTransition(): boolean {
  return (
    typeof document !== 'undefined' &&
    'startViewTransition' in document &&
    !(typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  );
}

/** Arms the reverse transition for one back navigation. No-op when unsupported, reduced-motion, or already armed. */
function armBackTransition(router: AnyRouter, restaurantId: number): void {
  if (disarmActive || !canRunViewTransition()) return;

  const previousDefault = router.options.defaultViewTransition;
  const root = document.documentElement;
  pendingReturnRestaurantId = restaurantId;
  router.options.defaultViewTransition = true;
  // Lets variables.css reverse the page-level slide for this transition only.
  root.dataset.vtDirection = 'back';

  let cleanupTimer: ReturnType<typeof setTimeout> | undefined;
  const disarm = () => {
    clearTimeout(cleanupTimer);
    unsubscribe();
    pendingReturnRestaurantId = null;
    claimedNodes.forEach((node) => node.style.removeProperty('view-transition-name'));
    claimedNodes = [];
    router.options.defaultViewTransition = previousDefault;
    delete root.dataset.vtDirection;
    disarmActive = null;
  };
  disarmActive = disarm;

  const unsubscribe = router.subscribe('onResolved', () => {
    unsubscribe();
    clearTimeout(cleanupTimer);
    cleanupTimer = setTimeout(disarm, RETURN_CLEANUP_DELAY_MS);
  });
  // Safety net: the navigation never resolved (blocked / cancelled), so the
  // router-driven disarm never ran. Leave a trace so a stuck arm is visible.
  cleanupTimer = setTimeout(() => {
    if (import.meta.env.DEV) {
      console.debug('[viewTransition] back transition safety timeout fired; disarming');
    }
    disarm();
  }, RETURN_SAFETY_TIMEOUT_MS);
}

/**
 * Disarms any in-flight reverse transition immediately: clears
 * `data-vt-direction`, restores `defaultViewTransition`, clears timers and
 * releases claimed view-transition names. Call it before starting a forward
 * navigation (a card tap within the ~800ms post-back cleanup window) so the
 * forward morph doesn't play the reversed slide or lose its names. No-op when
 * nothing is armed.
 */
export function cancelBackTransition(): void {
  disarmActive?.();
}

const RESTAURANT_PROFILE_PATH = /^\/restaurants\/(\d+)\/?$/;
const installedRouters = new WeakSet<AnyRouter>();

/**
 * For in-app back controls (the hero back arrow): arms the reverse transition
 * synchronously, BEFORE `router.history.back()`.
 *
 * Verified against @tanstack/history 1.139 / router-core 1.139: `back()` calls
 * `window.history.back()` (with `notifyOnIndexChange: false`) and the router
 * reads `defaultViewTransition` in `startViewTransition`, which runs at
 * `onReady` AFTER `loadMatches`. So the capture-phase popstate listener in
 * `installBackTransition` is probably already early enough on its own. This
 * explicit arm is kept as a safety net (it does not depend on event ordering)
 * and is a no-op if the listeners already armed.
 */
export function startBackTransition(router: AnyRouter, restaurantId: number): void {
  if (!router.history.canGoBack()) return;
  armBackTransition(router, restaurantId);
}

/** The slice of the Navigation API we use (Chromium; not in TS's lib.dom). */
interface NavigationLike extends EventTarget {
  currentEntry: { index: number } | null;
}
interface NavigateEventLike extends Event {
  navigationType: string;
  destination: { index: number; sameDocument: boolean };
  /** True when the browser already animated this navigation (iOS/Android edge-swipe back). Baseline 2026. */
  hasUAVisualTransition?: boolean;
}

/**
 * Idempotent; call once from the restaurant profile. Arms the reverse
 * transition when the member uses the browser's own back button/swipe away
 * from a restaurant profile (not on forward traversals or other pages), where
 * no in-app handler runs first. Skipped when the browser already played its
 * own transition (`hasUAVisualTransition`, e.g. iOS Safari edge-swipe), which
 * would otherwise double-animate.
 *
 * Two listeners, long-lived rather than a mount/unmount effect (the profile's
 * effect cleanup runs before popstate, so a component-scoped listener would
 * already be gone). The Navigation API's `navigate` event fires synchronously
 * at the start of the traversal, so it is the earliest hook where available;
 * the capture-phase popstate listener covers browsers without it and runs
 * before the router's own bubbling handler. The departing page is read from
 * the router's `resolvedLocation` (the page still on screen). Both listeners
 * no-op for every other page, so leaving them installed is safe.
 */
export function installBackTransition(router: AnyRouter): void {
  if (typeof window === 'undefined' || installedRouters.has(router)) return;
  installedRouters.add(router);

  const armIfLeavingProfile = () => {
    const match = RESTAURANT_PROFILE_PATH.exec(router.state.resolvedLocation?.pathname ?? '');
    if (match) armBackTransition(router, Number(match[1]));
  };

  const navigation = (window as Window & { navigation?: NavigationLike }).navigation;
  navigation?.addEventListener('navigate', (event) => {
    const { navigationType, destination } = event as NavigateEventLike;
    const currentIndex = navigation.currentEntry?.index;
    if (navigationType !== 'traverse' || !destination.sameDocument) return;
    if ((event as NavigateEventLike).hasUAVisualTransition) return;
    if (currentIndex == null || destination.index >= currentIndex) return;
    armIfLeavingProfile();
  });

  // Capture phase so we run before the router's own (bubbling) popstate handler.
  window.addEventListener(
    'popstate',
    (event) => {
      // PopStateEvent.hasUAVisualTransition is absent in older browsers (undefined -> falsy).
      if ((event as PopStateEvent & { hasUAVisualTransition?: boolean }).hasUAVisualTransition) return;
      const departingIndex = router.state.resolvedLocation?.state.__TSR_index;
      const nextIndex = window.history.state?.__TSR_index;
      if (typeof nextIndex === 'number' && typeof departingIndex === 'number' && nextIndex >= departingIndex) return;
      armIfLeavingProfile();
    },
    true,
  );
}

/** True while a back transition is waiting for this restaurant's card. Safe to read during render. */
export function isReturnTransitionTarget(restaurantId: number): boolean {
  return pendingReturnRestaurantId === restaurantId;
}

/**
 * Called by a mounting card (layout effect, so before the "new" snapshot is
 * captured). Only the first card for the pending restaurant wins and the
 * claim is consumed, which keeps names unique even when the same restaurant
 * appears twice on the destination page. Skipped if the card is entirely
 * outside the viewport, where a morph target would be meaningless.
 */
export function claimReturnTransitionNames(
  restaurantId: number,
  photoRef: RefObject<HTMLElement | null>,
  titleRef: RefObject<HTMLElement | null>,
): void {
  if (pendingReturnRestaurantId !== restaurantId) return;
  pendingReturnRestaurantId = null;
  const rect = photoRef.current?.getBoundingClientRect();
  if (!rect || rect.bottom < 0 || rect.top > window.innerHeight) return;
  const { photo, title } = getRestaurantTransitionNames(restaurantId);
  assignViewTransitionName(photoRef, photo);
  assignViewTransitionName(titleRef, title);
  claimedNodes = [photoRef.current, titleRef.current].filter((node): node is HTMLElement => node != null);
}
